import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { PUBLIC_PROSPECT_CATEGORIES, SELECT_SEARCH_THRESHOLD } from '../../../constants';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { setProspectFooterFields, setProspectForm } from '../../../redux/reducers';
import { useGetPracticeProspectByIdMutation, useGetPracticeProspectsForSelectQuery } from '../../../services';
import { Permissions, ProspectCategory, ProspectPageTabs, ProspectType } from '../../../types';
import { conditionalObject, parseProspectToOption } from '../../../utils';
import { ButtonGroup, OrgSelector, Select, TextButton, Typography, TypographyType } from '../../shared';

interface CreateFromExistingSimulationProps {
  setVisitedTabs: Dispatch<SetStateAction<Set<ProspectPageTabs>>>;
  filterProspectCategories: (category: ProspectCategory) => boolean;
  goToPrevStep: () => void;
  targetOrgId?: string;
}

const CreateFromExistingSimulation = ({
  filterProspectCategories,
  goToPrevStep,
  setVisitedTabs,
  targetOrgId,
}: CreateFromExistingSimulationProps) => {
  const [templateProspectSearchValue, setTemplateProspectSearchValue] = useState('');
  const [templateOrgId, setTemplateOrgId] = useState<string | undefined>(undefined);
  const [templateProspectId, setTemplateProspectId] = useState<string | undefined>(undefined);

  const dispatch = useAppDispatch();

  const { user, organization } = useAppSelector((state) => state.auth);

  const permissions = user?.permissions || [];
  const isSuperAdmin = permissions.includes(Permissions.ADMIN_ACCESS);
  const canManageProspect = permissions.includes(Permissions.MANAGE_PROSPECT);

  const isCustomSimsEnabled = !!organization?.isCustomSimsEnabled;

  // If the user is a super-admin, they need to select an organization before selecting a prospect.
  const isProspectSelectDisabled = isSuperAdmin && !templateOrgId;
  const categories = PUBLIC_PROSPECT_CATEGORIES.filter(filterProspectCategories);
  const types = Object.values(ProspectType).filter(
    (type) => type !== ProspectType.CUSTOM || isCustomSimsEnabled || isSuperAdmin
  );

  const [getPracticeProspectById, { isLoading: isProspectLoading }] = useGetPracticeProspectByIdMutation();

  // Get the prospects for the selected organization.
  // If the user is not a super-admin, they can only select prospects from their own organization.
  const { data: prospects = [], isLoading: isProspectsLoading } = useGetPracticeProspectsForSelectQuery(
    { ...conditionalObject(!!templateOrgId, { orgId: templateOrgId }), categories, types },
    { skip: isProspectSelectDisabled }
  );

  const prospectOptions = useMemo(() => prospects.map((p) => parseProspectToOption(p)), [prospects]);

  const isProspectSearchEnabled = prospectOptions.length > SELECT_SEARCH_THRESHOLD;
  const prospectSearchProps = isProspectSearchEnabled
    ? { searchValue: templateProspectSearchValue, setSearchValue: setTemplateProspectSearchValue }
    : undefined;

  const onOrgChange = useCallback(
    (newValue?: string) => {
      setTemplateOrgId(newValue);
      // Reset the prospect ID when the organization changes.
      setTemplateProspectId(undefined);
    },
    [setTemplateOrgId, setTemplateProspectId]
  );

  const onNext = useCallback(async () => {
    if (!templateProspectId) return;
    // If the user has selected a template prospect, fetch the prospect data and set the form state.
    const { data: prospect } = await getPracticeProspectById(templateProspectId);
    if (prospect && !!targetOrgId) {
      const { notes, tags, ...rest } = prospect;
      dispatch(
        setProspectForm({
          prospect: { ...rest, orgId: targetOrgId },
          canManageProspect,
          isTemplate: true,
        })
      );
      dispatch(setProspectFooterFields({ isFavorited: false, notes, tags }));
      // Mark all tabs as visited except for the "Prompt" tab since we are creating a new prospect, but with preset data.
      setVisitedTabs(new Set(Object.values(ProspectPageTabs).slice(0, -1)));
    }
  }, [templateProspectId, dispatch, canManageProspect, targetOrgId, getPracticeProspectById]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <Typography type={TypographyType.BASE}>Which simulation do you want to duplicate?*</Typography>
        {isSuperAdmin && <OrgSelector clearable selectedOrgId={templateOrgId} onChange={onOrgChange} />}
        <Select
          disabled={isProspectSelectDisabled}
          loading={isProspectsLoading}
          placeholder="Select simulation"
          options={prospectOptions}
          searchProps={prospectSearchProps}
          selected={templateProspectId}
          onChange={(newValue) => setTemplateProspectId(newValue)}
        />
      </div>
      <ButtonGroup loading={isProspectLoading}>
        <TextButton onClick={onNext} text="Next" disabled={!templateProspectId} />
        <TextButton onClick={goToPrevStep} text="Back" />
      </ButtonGroup>
    </div>
  );
};

export default CreateFromExistingSimulation;
